// @ts-strict-ignore
import {AfterViewInit, Component, computed, effect, inject, input, signal} from "@angular/core";
import {configureAssessmentProviders} from "../services/assessable.configuration";
import {Translations} from "@translations";
import {AssessableItemGroup, AssessmentItem, UserRecordingAssessment} from "@models";
import {AssessmentContextService} from "../services/assessment-context.service";
import {AssessableUiService} from "../services/assessable-ui.service";
import {AssessmentAnswerService} from "../services/assessment-answer.service";
import {AssessablePanelMode} from "@common/components/assessment/utils";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {AssessableItemData} from "../assessable-item-template.directive";

type SingleItemData = {
	group: AssessableItemGroup<AssessmentItem>;
	item: AssessmentItem;
};

@UntilDestroy()
@Component({
	selector: "sim-assessment-answer-single-item-panel",
	templateUrl: "./assessment-answer-single-item-panel.component.html",
	styleUrl: "./assessment-answer-single-item-panel.component.scss",
	animations: [zoomInOnEnterAnimation({duration: 250}), zoomOutOnLeaveAnimation({duration: 250})],
	providers: [...configureAssessmentProviders()],
	standalone: false,
})
export class AssessmentAnswerSingleItemPanelComponent implements AfterViewInit {
	protected readonly T = Translations;

	public readonly assessment = input.required<UserRecordingAssessment>();
	public readonly mode = input.required<AssessablePanelMode>();

	private _contextService = inject(AssessmentContextService);
	private _store = inject(AssessableUiService);
	private _answerService = inject(AssessmentAnswerService);

	private _currentItemId = signal<string | null>(null);
	public readonly currentItemId = this._currentItemId.asReadonly();
	public readonly currentGroupId = computed(() => this._computeCurrentGroupId());
	public readonly currentItemData = computed(() => this._computeCurrentItemData());
	public readonly currentItemAssessmentData = computed(() =>
		this._computeCurrentItemAssessmentData(this.currentItemData(), this.currentItemIndex(), this.currentGroupIndex()),
	);
	public readonly currentGroupIndex = computed(() => this.assessment().assessableGroups.indexOf(this.currentItemData().group));
	public readonly currentItemIndex = computed(() => this.currentItemData().group.items.indexOf(this.currentItemData().item));
	public readonly isNextItemButtonDisabled = computed(() => this._computeIsNextItemButtonDisabled());
	public readonly isFormInitialized = this._answerService.isInitialized;
	public readonly $changedAssessment = this._answerService.$assessable;

	constructor() {
		effect(
			() => {
				const assessment = this.assessment();
				this._contextService.initialize(assessment, assessment.competenceCategoryIds);
				this._answerService.initialize(this.assessment);
				this._answerService.$savingPipeline.pipe(untilDestroyed(this)).subscribe();
			},
			{
				allowSignalWrites: true,
			},
		);

		effect(
			() => {
				this._store.setMode(this.mode());
			},
			{
				allowSignalWrites: true,
			},
		);
	}

	public ngAfterViewInit(): void {
		const assessment = this.assessment();
		const items = assessment.assessableGroups.flatMap((group) => group.items).concat(assessment.ungroupedItems);
		if (items.length > 0) {
			this._currentItemId.set(items[0].assessableItemId);
		}
	}

	public moveToNextItem() {
		const assessment = this.assessment();
		const currentItemData = this.currentItemData();
		if (!currentItemData) {
			return;
		}

		if (this._canMoveToNextItemInGroup(currentItemData)) {
			this._currentItemId.set(currentItemData.group.items[this.currentItemIndex() + 1].assessableItemId);
		} else if (this._canMoveToNextGroup(assessment, currentItemData)) {
			this._currentItemId.set(assessment.assessableGroups[this.currentGroupIndex() + 1].items[0].assessableItemId);
		}
	}

	public moveToItem(itemId: string) {
		this._currentItemId.set(itemId);
	}

	private _computeCurrentItemData(): SingleItemData | null {
		const visibleItemId = this._currentItemId();
		const assessment = this.assessment();

		if (!visibleItemId || !assessment) {
			return null;
		}

		const items: SingleItemData[] = assessment.assessableGroups.flatMap((group) => {
			return group.items.map((item) => ({
				group,
				item,
			}));
		});
		return items.find((singleItemData) => singleItemData.item.assessableItemId === visibleItemId) ?? null;
	}

	private _computeCurrentItemAssessmentData(
		itemData: SingleItemData,
		itemIndex: number,
		groupIndex: number,
	): AssessableItemData<AssessmentItem> {
		return {
			item: itemData.item,
			groupId: itemData.group.groupId,
			dragHandle: null,
			length: itemData.group.items.length,
			index: itemIndex,
			groupItems: itemData.group.items,
			groupIndex: groupIndex,
		};
	}

	private _canMoveToNextItemInGroup(currentItemData: SingleItemData): boolean {
		const currentItemIndex = currentItemData.group.items.indexOf(currentItemData.item);
		return currentItemIndex + 1 < currentItemData.group.items.length;
	}

	private _canMoveToNextGroup(assessment: UserRecordingAssessment, currentItemData: SingleItemData): boolean {
		const currentGroupIndex = assessment.assessableGroups.indexOf(currentItemData.group);
		return (
			currentGroupIndex + 1 < assessment.assessableGroups.length &&
			assessment.assessableGroups[currentGroupIndex + 1].items.length > 0
		);
	}

	private _computeIsNextItemButtonDisabled(): boolean {
		const currentItemData = this.currentItemData();
		const assessment = this.assessment();
		if (!currentItemData || !assessment) {
			return true;
		}

		return !this._canMoveToNextItemInGroup(currentItemData) && !this._canMoveToNextGroup(assessment, currentItemData);
	}

	private _computeCurrentGroupId() {
		const visibleItemId = this.currentItemId();
		const assessment = this.assessment();

		if (!visibleItemId || !assessment) {
			return null;
		}

		const group = assessment.assessableGroups.find((group) => group.items.some((item) => item.assessableItemId === visibleItemId));
		return group?.groupId ?? null;
	}
}
